import {
  AmbientLight,
  DirectionalLight,
  type OrthographicCamera,
  PerspectiveCamera,
  Ray,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
} from "three/webgpu";
import type { Color } from "../../common/color.js";
import { RenderTarget } from "../../engine/RenderTarget.js";
import { SceneRenderer, type BloomOptions } from "../../engine3d/SceneRenderer.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { MouseEventArgs, WheelEventArgs } from "../core/events.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

export interface Viewport3DOptions extends ControlOptions {
  /** The scene shown. A new one, lit by a soft key light, when absent. */
  scene?: Scene;
  camera?: PerspectiveCamera | OrthographicCamera;
  /** Where an orbit turns about. The origin by default. */
  target?: { x: number; y: number; z: number };
  /** Behind the scene. Transparent by default, so the window shows through. */
  clear?: Color;
  /** Bloom on whatever is brightest: options, or just a strength. 0 or null for none. */
  bloom?: BloomOptions | number | null;
  /** Drag to orbit the camera about `target`. */
  orbitOnDrag?: boolean;
  /** Wheel to dolly in and out. */
  zoomOnWheel?: boolean;
  minDistance?: number;
  maxDistance?: number;
  /**
   * Render the scene only when this viewport is invalidated or resized. Off by default.
   *
   * Without it the scene is rendered on every desktop repaint, including one made for a hover or a caret
   * somewhere else entirely. With it, those repaints show the last frame again; the price is that a change
   * to the scene shows only once `invalidate()` is called on this viewport - which a loop driving it
   * already does.
   */
  renderOnDemand?: boolean;
  /**
   * The share of the control's device pixels the scene is rendered at, `RENDER_SCALE_MIN` to
   * `RENDER_SCALE_MAX`; 1 by default. The frame is stretched to the control smoothly, so 0.75 draws about
   * half the pixels and a HUD over it stays sharp. Also settable at runtime.
   */
  renderScale?: number;
}

export const RENDER_SCALE_MIN = 0.25;
export const RENDER_SCALE_MAX = 2;

/**
 * The size in texels a viewport of `width` by `height` logical pixels renders at: through the UI scale,
 * the device pixel ratio and the render scale (held to its range), never under one texel.
 */
export function renderTargetSize(
  width: number,
  height: number,
  uiScale: number,
  dpr: number,
  renderScale = 1,
): { width: number; height: number } {
  const scale = uiScale * dpr * Math.min(RENDER_SCALE_MAX, Math.max(RENDER_SCALE_MIN, renderScale || 1));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

const MIN_PITCH = -Math.PI / 2 + 0.05;
const MAX_PITCH = Math.PI / 2 - 0.05;

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Bloom as options, from options or a bare strength. */
function bloomOf(v: BloomOptions | number | null | undefined): BloomOptions | null {
  if (typeof v === "number") return v > 0 ? { strength: v, radius: 0.5, threshold: 0.6 } : null;
  return v ?? null;
}

/** A scene with nothing in it but light enough to see by. */
function litScene(): Scene {
  const scene = new Scene();
  scene.add(new AmbientLight(0xffffff, 0.6));
  const key = new DirectionalLight(0xfff6e8, 2.2);
  key.position.set(4, 8, 4.5);
  scene.add(key);
  return scene;
}

/**
 * A window onto a three.js scene, as an ordinary Webwin control.
 *
 * The scene renders into a texture of its own, and what the UI draws is a bitmap, so it clips and
 * layers like any other control and takes UI laid over it.
 *
 * The built-in controls are an orbit camera, because that is what a viewport is usually for: looking at
 * a thing. A game wanting first-person motion drives `camera` itself and turns these off.
 */
export class Viewport3D extends Control {
  scene: Scene;
  camera: PerspectiveCamera | OrthographicCamera;
  /** What the orbit turns about. */
  readonly target: Vector3;
  orbitOnDrag: boolean;
  zoomOnWheel: boolean;
  minDistance: number;
  maxDistance: number;
  /** See `Viewport3DOptions.renderOnDemand`. */
  renderOnDemand: boolean;

  #renderer: SceneRenderer | null = null;
  #target: RenderTarget | null = null;
  #renderScale = 1;
  #clear: Color | undefined;
  #bloom: BloomOptions | null;

  /** Whether the frame held is out of date; see `renderOnDemand`. */
  #stale = true;
  #renderedWidth = 0;
  #renderedHeight = 0;

  #orbiting = false;
  #from = { x: 0, y: 0 };
  /** Spherical coordinates about the target. */
  #yaw = 0;
  #pitch = 0;
  #distance = 1;
  #raycaster = new Raycaster();

  constructor(options: Viewport3DOptions = {}) {
    super({ width: 480, height: 320, tabStop: true, ...options });
    this.scene = options.scene ?? litScene();
    if (options.camera) {
      this.camera = options.camera;
    } else {
      const camera = new PerspectiveCamera(45, 1.5, 0.1, 500);
      camera.position.set(4, 3, 6);
      this.camera = camera;
    }
    const t = options.target ?? { x: 0, y: 0, z: 0 };
    this.target = new Vector3(t.x, t.y, t.z);
    this.camera.lookAt(this.target);
    this.#clear = options.clear;
    this.#bloom = bloomOf(options.bloom);
    this.orbitOnDrag = options.orbitOnDrag ?? true;
    this.zoomOnWheel = options.zoomOnWheel ?? true;
    this.minDistance = options.minDistance ?? 0.5;
    this.maxDistance = options.maxDistance ?? 500;
    this.renderOnDemand = options.renderOnDemand ?? false;
    this.#renderScale = options.renderScale ?? 1;
    this.#readCamera();

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => this.#endOrbit());
    this.on("pointerleave", () => this.#endOrbit());
    this.on("wheel", (e) => this.#onWheel(e));
  }

  /**
   * Also marks the scene for re-rendering, which is the only thing that does when `renderOnDemand` is
   * set. The base constructor can invalidate before this class's fields exist, hence the guard.
   */
  override invalidate(): void {
    if (#stale in this) this.#stale = true;
    super.invalidate();
  }

  /** See `Viewport3DOptions.renderScale`. The next paint resizes the frame. */
  get renderScale(): number {
    return this.#renderScale;
  }

  set renderScale(value: number) {
    this.#renderScale = clamp(value || 1, RENDER_SCALE_MIN, RENDER_SCALE_MAX);
    this.invalidate();
  }

  get bloom(): BloomOptions | null {
    return this.#bloom;
  }

  set bloom(value: BloomOptions | number | null) {
    this.#bloom = bloomOf(value);
    if (this.#renderer) this.#renderer.bloom = this.#bloom;
    this.invalidate();
  }

  /** What renders the scene, from the first paint on. */
  get renderer(): SceneRenderer | null {
    return this.#renderer;
  }

  /** Draw calls and triangles in the last frame the renderer drew, whichever scene that was. */
  get stats(): { calls: number; triangles: number } {
    const info = this.desktop?.gpu.renderer.info.render;
    return { calls: info?.drawCalls ?? 0, triangles: info?.triangles ?? 0 };
  }

  /** A ray from a point in this control into the scene, for picking. */
  rayAt(local: { x: number; y: number }): Ray {
    const ndc = new Vector2((local.x / Math.max(1, this.width)) * 2 - 1, 1 - (local.y / Math.max(1, this.height)) * 2);
    this.#fitCamera();
    this.camera.updateMatrixWorld();
    this.#raycaster.setFromCamera(ndc, this.camera);
    return this.#raycaster.ray.clone();
  }

  /** A raycaster aimed from a point in this control, for `intersectObjects`. */
  raycasterAt(local: { x: number; y: number }): Raycaster {
    const ray = this.rayAt(local);
    this.#raycaster.ray.copy(ray);
    return this.#raycaster;
  }

  /** Where a world point lands in this control, or null when it is behind the camera. */
  project(world: { x: number; y: number; z: number }): { x: number; y: number } | null {
    this.#fitCamera();
    this.camera.updateMatrixWorld();
    const p = new Vector3(world.x, world.y, world.z).project(this.camera);
    if (p.z > 1 || p.z < -1) return null;
    return { x: ((p.x + 1) / 2) * this.width, y: ((1 - p.y) / 2) * this.height };
  }

  /** The whole scene is the drag, so a scrollable ancestor must not pan instead. */
  override handlesDragAt(): boolean {
    return this.orbitOnDrag;
  }

  #fitCamera(): void {
    const camera = this.camera;
    if (camera instanceof PerspectiveCamera) {
      const aspect = this.width / Math.max(1, this.height);
      if (camera.aspect !== aspect) {
        camera.aspect = aspect;
        camera.updateProjectionMatrix();
      }
    }
  }

  /** Read the camera's current placement back into orbit coordinates. */
  #readCamera(): void {
    const offset = this.camera.position.clone().sub(this.target);
    this.#distance = Math.max(1e-3, offset.length());
    this.#pitch = Math.asin(clamp(offset.y / this.#distance, -1, 1));
    this.#yaw = Math.atan2(offset.x, offset.z);
  }

  /** Put the camera back where the orbit coordinates say it should be. */
  #applyOrbit(): void {
    const cosPitch = Math.cos(this.#pitch);
    this.camera.position.set(
      this.target.x + this.#distance * cosPitch * Math.sin(this.#yaw),
      this.target.y + this.#distance * Math.sin(this.#pitch),
      this.target.z + this.#distance * cosPitch * Math.cos(this.#yaw),
    );
    this.camera.lookAt(this.target);
    this.invalidate();
  }

  #onPointerDown(e: MouseEventArgs): void {
    this.focus();
    if (!this.orbitOnDrag) return;
    // Re-read first: game code may have moved the camera since the last drag.
    this.#readCamera();
    this.#orbiting = true;
    this.#from = { x: e.x, y: e.y };
    this.desktop?.input.setCapture(this);
  }

  #onPointerMove(e: MouseEventArgs): void {
    if (!this.#orbiting) return;
    const dx = e.x - this.#from.x;
    const dy = e.y - this.#from.y;
    this.#from = { x: e.x, y: e.y };
    // A full drag across the control is about half a turn: turning an object rather than flinging it.
    this.#yaw -= (dx / Math.max(1, this.width)) * Math.PI * 2;
    // Clamped short of the poles, where the up vector and the view direction are parallel.
    this.#pitch = clamp(this.#pitch + (dy / Math.max(1, this.height)) * Math.PI, MIN_PITCH, MAX_PITCH);
    this.#applyOrbit();
  }

  #endOrbit(): void {
    if (!this.#orbiting) return;
    this.#orbiting = false;
    this.desktop?.input.releaseCapture(this);
  }

  #onWheel(e: WheelEventArgs): void {
    if (!this.zoomOnWheel || e.deltaY === 0) return;
    this.#readCamera();
    // Multiplicative, so each notch moves the same proportion of the way in.
    this.#distance = clamp(this.#distance * (e.deltaY > 0 ? 1.12 : 1 / 1.12), this.minDistance, this.maxDistance);
    this.#applyOrbit();
    e.handled = true;
  }

  protected override onEnvironmentChanged(): void {
    if (this.desktop) return;
    this.#renderer?.dispose();
    this.#renderer = null;
    this.#target?.dispose();
    this.#target = null;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const gpu = this.desktop?.gpu;
    const r = this.localRect;
    if (!gpu || r.width <= 0 || r.height <= 0) return;

    const { width, height } = renderTargetSize(r.width, r.height, rc.scale, gpu.size.dpr, this.#renderScale);
    if (!this.#target) {
      this.#target = new RenderTarget(gpu, { width, height, depth: true, premultiplied: true, label: "webwin.viewport3d" });
      this.#stale = true;
    } else {
      this.#target.resize(width, height);
    }
    if (!this.#renderer) {
      this.#renderer = new SceneRenderer(gpu, { scene: this.scene, camera: this.camera, clear: this.#clear ?? null, bloom: this.#bloom });
      this.#stale = true;
    }

    const renderer = this.#renderer;
    const target = this.#target;
    if (!this.renderOnDemand || this.#stale || target.width !== this.#renderedWidth || target.height !== this.#renderedHeight) {
      renderer.scene = this.scene;
      renderer.camera = this.camera;
      renderer.render(target);
      this.#stale = false;
      this.#renderedWidth = target.width;
      this.#renderedHeight = target.height;
    }
    rc.drawTexture(target, r);

    if (this.focused) theme.drawFocusRing(rc, r);
  }
}
